import { logger } from '@beelday/common'
import { MutableRefObject } from 'react'
import {
	isSilent,
	reopenDelay,
	shouldReopenOnReturn,
	StreamHealth,
} from './model/stream-health'

// `EventSource.OPEN` and `.CLOSED`, without needing an `EventSource` to read them from.
const OPEN = 1
const CLOSED = 2

/**
 * - `opened`: a stream is up for the first time where the room is;
 * - `lost`: the stream stopped delivering, and a new one is on its way;
 * - `reopened`: a stream is up again after one was lost - whatever was
 *   read once may have changed in between.
 */
export type StreamStatus = 'opened' | 'lost' | 'reopened'

export type StreamSupervisorOptions = {
	/**
	 * Opens the stream for where the room is now, or nothing while there is
	 * nowhere to connect.
	 */
	open: () => EventSource | undefined
	/** Settles the login before a stream is opened again. */
	ensureFreshLogin: () => Promise<void>
	onStatus: (status: StreamStatus) => void
	/** Silence after which a stream is dead: the server beats every 20 s. */
	timeoutMs: number
}

export type StreamSupervisor = {
	/** Opens a stream where the room is now, closing the one before. */
	connect: () => void
	/** Replaces the stream now, the login refreshed first if it needs it. */
	reopen: () => void
	/** Run every few seconds: a silent stream is replaced. */
	watch: () => void
	/** The page is back after `awayMs`: replaces a stream that may have missed events. */
	onReturn: (awayMs: number) => void
	/** Closes the stream, and anything on its way to reopen it. */
	stop: () => void
}

/**
 * Keeps the room's event stream alive. The browser retries a stream by
 * itself only after a plain network error; a stream it closes for good -
 * Safari after the app was in the background, or a reconnect refused - stays
 * closed. This opens a new one, quickly at first and then twice a minute,
 * replaces a stream that has gone silent, and one that may have missed
 * events while the page was away. Every new stream starts with the room's
 * whole state, so the room catches up by itself.
 *
 * `streamRef` always holds the stream open now.
 */
export const createStreamSupervisor = (
	options: () => StreamSupervisorOptions,
	streamRef: MutableRefObject<EventSource | undefined>
): StreamSupervisor => {
	let lastSignOfLifeAt = 0
	let attempt = 0
	let lost = false
	let retry: ReturnType<typeof setTimeout> | undefined
	// Bumped by every connect and stop, so a reopen that waited for the login
	// does not open a stream nobody wants any more.
	let generation = 0

	const cancelRetry = () => {
		if (retry) {
			clearTimeout(retry)
			retry = undefined
		}
	}

	const health = (): StreamHealth => ({
		open: streamRef.current?.readyState === OPEN,
		lastSignOfLifeAt,
	})

	const markLost = () => {
		if (!lost) {
			lost = true
			options().onStatus('lost')
		}
	}

	const connect = () => {
		generation += 1
		cancelRetry()
		streamRef.current?.close()
		const source = options().open()
		streamRef.current = source
		lastSignOfLifeAt = Date.now()
		if (!source) return

		const current = () => streamRef.current === source
		source.addEventListener('open', () => {
			if (!current()) return
			lastSignOfLifeAt = Date.now()
			attempt = 0
			options().onStatus(lost ? 'reopened' : 'opened')
			lost = false
		})
		source.addEventListener('SERVER_BEAT', () => {
			if (current()) lastSignOfLifeAt = Date.now()
		})
		source.addEventListener('error', () => {
			if (!current()) return
			markLost()
			// While connecting, the browser is already trying again.
			if (source.readyState === CLOSED) scheduleReopen()
		})
	}

	const reopen = () => {
		cancelRetry()
		const mine = generation
		options()
			.ensureFreshLogin()
			.catch(error => logger.warn('Could not refresh the login', error))
			.then(() => {
				if (mine === generation) connect()
			})
	}

	const scheduleReopen = () => {
		if (retry) return
		retry = setTimeout(() => {
			retry = undefined
			reopen()
		}, reopenDelay(attempt))
		attempt += 1
	}

	const watch = () => {
		if (
			streamRef.current &&
			isSilent(health(), Date.now(), options().timeoutMs)
		) {
			markLost()
			scheduleReopen()
		}
	}

	const onReturn = (awayMs: number) => {
		if (!streamRef.current) return
		const stale = shouldReopenOnReturn({
			health: health(),
			now: Date.now(),
			timeoutMs: options().timeoutMs,
			awayMs,
		})
		if (stale) {
			attempt = 0
			markLost()
			reopen()
		}
	}

	const stop = () => {
		generation += 1
		cancelRetry()
		streamRef.current?.close()
		streamRef.current = undefined
	}

	return { connect, reopen, watch, onReturn, stop }
}
